// 明暗模式持久化的 localStorage key，需与 index.html 内联脚本中的 key 保持一致
const THEME_MODE_KEY = 'theme-mode'

// 主题色：新 logo（CINEIGA）红。深色模式用提亮的同色系红，保证暗背景上文字/图标可读。
export const LIGHT_PRIMARY_COLOR = '#CC251B'
export const DARK_PRIMARY_COLOR = '#E5453B'

// 记录用户所选的浅色主题色（供明暗切换时按当前模式重新应用 --el-color-primary）
let currentThemeColor = LIGHT_PRIMARY_COLOR

// 读取已保存的明暗模式，默认浅色模式
export function getThemeMode() {
  return localStorage.getItem(THEME_MODE_KEY) === 'dark' ? 'dark' : 'light'
}

/**
 * 应用明暗模式：切换 html 的 dark class（同时驱动 Element Plus 暗色变量与自定义设计令牌）
 * @param {'dark'|'light'} mode
 * @param {{ persist?: boolean }} [options] persist 默认 true；登录页临时强制 light 时应传 false，避免覆盖用户偏好
 */
export function applyThemeMode(mode, options = {}) {
  const { persist = true } = options
  const normalized = mode === 'dark' ? 'dark' : 'light'
  document.documentElement.classList.toggle('dark', normalized === 'dark')
  if (persist) {
    localStorage.setItem(THEME_MODE_KEY, normalized)
  }
  // 明暗切换后按当前模式重新应用主题色：--el-color-primary 是内联样式，需显式刷新
  // （light → 红，dark → 黄，保证深色模式不受浅色红主题影响）
  handleThemeStyle(currentThemeColor)
  // 清除 Element Plus 表格在主题切换前残留的 hover 内联背景，避免出现半页深/浅错乱
  clearElementTableHoverResidue()
  return normalized
}

/** 登录/注册等页面：临时强制 light，不写入 localStorage */
export function forceLightThemeTemporarily() {
  applyThemeMode('light', { persist: false })
}

/** 离开登录页后：按已保存偏好恢复明暗，不重复写入 */
export function restorePersistedThemeMode() {
  return applyThemeMode(getThemeMode(), { persist: false })
}

function clearElementTableHoverResidue() {
  document.querySelectorAll(
    '.el-table__body tr.hover-row, .el-table__fixed-body-wrapper tr.hover-row, .el-table__body tr.current-row'
  ).forEach((tr) => {
    tr.classList.remove('hover-row')
  })
  document.querySelectorAll(
    '.el-table__body td.el-table__cell, .el-table__fixed-body-wrapper td.el-table__cell'
  ).forEach((td) => {
    if (td.style && (td.style.backgroundColor || td.style.background)) {
      td.style.removeProperty('background-color')
      td.style.removeProperty('background')
    }
  })
}

// 处理主题样式
// theme 为浅色模式主题色；深色模式暂不修改，强制沿用原品牌黄（DARK_PRIMARY_COLOR）。
export function handleThemeStyle(theme) {
	if (theme) {
		currentThemeColor = theme
	}
	const isDark = document.documentElement.classList.contains('dark')
	const primary = isDark ? DARK_PRIMARY_COLOR : (currentThemeColor || LIGHT_PRIMARY_COLOR)
	document.documentElement.style.setProperty('--el-color-primary', primary)
	for (let i = 1; i <= 9; i++) {
		document.documentElement.style.setProperty(`--el-color-primary-light-${i}`, `${getLightColor(primary, i / 10)}`)
	}
	for (let i = 1; i <= 9; i++) {
		document.documentElement.style.setProperty(`--el-color-primary-dark-${i}`, `${getDarkColor(primary, i / 10)}`)
	}
}

// hex颜色转rgb颜色
export function hexToRgb(str) {
	str = str.replace('#', '')
	let hexs = str.match(/../g)
	for (let i = 0; i < 3; i++) {
		hexs[i] = parseInt(hexs[i], 16)
	}
	return hexs
}

// rgb颜色转Hex颜色
export function rgbToHex(r, g, b) {
	let hexs = [r.toString(16), g.toString(16), b.toString(16)]
	for (let i = 0; i < 3; i++) {
		if (hexs[i].length == 1) {
			hexs[i] = `0${hexs[i]}`
		}
	}
	return `#${hexs.join('')}`
}

// 变浅颜色值
export function getLightColor(color, level) {
	let rgb = hexToRgb(color)
	for (let i = 0; i < 3; i++) {
		rgb[i] = Math.floor((255 - rgb[i]) * level + rgb[i])
	}
	return rgbToHex(rgb[0], rgb[1], rgb[2])
}

// 变深颜色值
export function getDarkColor(color, level) {
	let rgb = hexToRgb(color)
	for (let i = 0; i < 3; i++) {
		rgb[i] = Math.floor(rgb[i] * (1 - level))
	}
	return rgbToHex(rgb[0], rgb[1], rgb[2])
}
